/*
  HandwritingReviewDialog — side-by-side original + extracted blocks.

  Left pane: the document original (single image page swipeable by page
  number). Right pane: the list of extracted blocks grouped by section,
  each with per-field confidence chip, illegible mark, "sensitive" flag
  and inline editor.

  Actions:
  - Confirm / Correct / Illegible / Reject on each block
  - Approve document (gated by the sensitive-block rule)
  - Generate draft SOAP note (post-approval)
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import HandwritingMappingDialog from "./HandwritingMappingDialog";
import CombinedDraftDialog from "./CombinedDraftDialog";
import {
  ChevronLeft, ChevronRight, Loader2, CheckCircle2, XCircle, EyeOff,
  Sparkles, ShieldAlert, RotateCw, ZoomIn, ZoomOut, Users, Merge,
} from "lucide-react";
import { cn } from "@/lib/utils";

const CONFIDENCE_TONE = {
  high:       "bg-emerald-50 text-emerald-800 border-emerald-300",
  medium:     "bg-amber-50 text-amber-800 border-amber-300",
  low:        "bg-orange-50 text-orange-800 border-orange-300",
  illegible:  "bg-red-50 text-red-800 border-red-300",
};

const REVIEW_TONE = {
  pending:    "bg-zinc-100 text-zinc-700 border-zinc-300",
  confirmed:  "bg-emerald-50 text-emerald-800 border-emerald-300",
  corrected:  "bg-sky-50 text-sky-800 border-sky-300",
  illegible:  "bg-red-50 text-red-800 border-red-300",
  rejected:   "bg-zinc-200 text-zinc-500 border-zinc-300 line-through",
};

const SECTION_ORDER = [
  "personal", "emergency_contact", "referral", "medical_history",
  "medications", "allergies", "measurements",
  "subjective", "objective", "assessment", "plan",
  "consent", "signature", "other",
];

function sectionLabel(s) {
  return (s || "other").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function HandwritingReviewDialog({ uploadId, open, onOpenChange, apiBase }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pageBlobUrl, setPageBlobUrl] = useState(null);
  const [saving, setSaving] = useState(null);
  const [approving, setApproving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [hoverBlockId, setHoverBlockId] = useState(null);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [combinedOpen, setCombinedOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/handwriting/uploads/${uploadId}`);
      setDoc(r.data);
    } catch (e) {
      toast.error("Couldn't load extraction", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [uploadId]);

  useEffect(() => { if (open && uploadId) load(); }, [open, uploadId, load]);

  // Fetch the current page image (blob so we get auth cookies).
  useEffect(() => {
    let cancelled = false;
    async function fetchPage() {
      if (!doc || !page) return;
      try {
        const r = await api.get(`/handwriting/uploads/${uploadId}/page/${page}`, {
          responseType: "blob",
        });
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        setPageBlobUrl((old) => {
          if (old) URL.revokeObjectURL(old);
          return url;
        });
      } catch {
        // Page render can fail if extraction is still running; keep silent
        setPageBlobUrl(null);
      }
    }
    fetchPage();
    return () => { cancelled = true; };
  }, [doc, page, uploadId]);

  useEffect(() => () => {
    if (pageBlobUrl) URL.revokeObjectURL(pageBlobUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const blocks = doc?.blocks || [];
  const grouped = useMemo(() => {
    const byPage = blocks.filter((b) => (b.page || 1) === page);
    const bySec = {};
    for (const b of byPage) (bySec[b.section || "other"] ||= []).push(b);
    return SECTION_ORDER
      .map((s) => [s, bySec[s]])
      .filter(([, list]) => list && list.length > 0);
  }, [blocks, page]);

  const pendingSensitiveCount = blocks.filter(
    (b) => b.is_sensitive && b.review_status === "pending",
  ).length;

  async function patchBlock(blockId, payload) {
    setSaving(blockId);
    try {
      const r = await api.patch(
        `/handwriting/uploads/${uploadId}/blocks/${blockId}`,
        payload,
      );
      // Update in place
      setDoc((d) => ({
        ...d,
        blocks: d.blocks.map((b) => (b.block_id === blockId ? r.data.block : b)),
      }));
    } catch (e) {
      toast.error("Couldn't save review", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(null);
    }
  }

  async function approve() {
    setApproving(true);
    try {
      await api.post(`/handwriting/uploads/${uploadId}/approve`);
      toast.success("Document approved & filed on the client record");
      await load();
    } catch (e) {
      toast.error("Couldn't approve", { description: e?.response?.data?.detail || e.message });
    } finally {
      setApproving(false);
    }
  }

  async function draftSoap() {
    setDrafting(true);
    try {
      const r = await api.post(`/handwriting/uploads/${uploadId}/generate-soap`);
      toast.success("Draft SOAP note created", {
        description: `Draft ready under Notes / Forms · ID ${r.data.soap_note_id.slice(0, 8)}…`,
      });
      await load();
    } catch (e) {
      toast.error("Draft failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setDrafting(false);
    }
  }

  const isApproved = doc?.status === "approved";
  const canApprove = !isApproved && pendingSensitiveCount === 0 && blocks.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[92vw] max-h-[92vh] overflow-hidden flex flex-col" data-testid="hw-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-700" />
            {doc?.filename || "Review handwritten document"}
            {isApproved && <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-300 text-2xs uppercase">Approved</Badge>}
          </DialogTitle>
          <DialogDescription>
            {doc?.provider && doc?.model
              ? `Extracted by ${doc.provider} / ${doc.model}`
              : "Handwriting extraction results"}
          </DialogDescription>
        </DialogHeader>

        {loading || !doc ? (
          <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3 overflow-hidden">
            {/* ---------- Left: original document ---------- */}
            <div className="bg-zinc-900/95 rounded-md overflow-auto relative flex flex-col" data-testid="hw-original-pane">
              <div className="flex items-center justify-between px-2 py-1.5 bg-zinc-900 text-zinc-100 text-xs sticky top-0 z-10">
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-zinc-100 hover:bg-zinc-800"
                          disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span>
                    Page {page} of {doc.page_count || 1}
                  </span>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-zinc-100 hover:bg-zinc-800"
                          disabled={page >= (doc.page_count || 1)}
                          onClick={() => setPage((p) => p + 1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-zinc-100 hover:bg-zinc-800"
                          onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}>
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span>{Math.round(zoom * 100)}%</span>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-zinc-100 hover:bg-zinc-800"
                          onClick={() => setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))}>
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-zinc-100 hover:bg-zinc-800"
                          onClick={() => setRotate((r) => (r + 90) % 360)}>
                    <RotateCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="flex-1 flex items-start justify-center overflow-auto p-3">
                {pageBlobUrl ? (
                  <div className="relative" style={{ transform: `rotate(${rotate}deg) scale(${zoom})`, transformOrigin: "top center" }}>
                    <img
                      src={pageBlobUrl}
                      alt="Original document"
                      className="max-w-full block"
                      data-testid="hw-original-image"
                    />
                    {/* bbox highlight for hovered block */}
                    {hoverBlockId && (() => {
                      const b = blocks.find((x) => x.block_id === hoverBlockId);
                      if (!b?.bbox) return null;
                      const { x, y, w, h } = b.bbox;
                      return (
                        <div
                          className="absolute border-2 border-indigo-400 bg-indigo-300/20 pointer-events-none rounded"
                          style={{
                            left: `${(x / 1000) * 100}%`, top: `${(y / 1000) * 100}%`,
                            width: `${(w / 1000) * 100}%`, height: `${(h / 1000) * 100}%`,
                          }}
                        />
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-zinc-400 text-sm py-8">Rendering page…</div>
                )}
              </div>
            </div>

            {/* ---------- Right: extracted blocks ---------- */}
            <div className="overflow-y-auto pr-1" data-testid="hw-blocks-pane">
              {doc.status === "processing" && (
                <div className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded p-2 mb-3">
                  <Loader2 className="h-3.5 w-3.5 inline mr-1 animate-spin" />
                  Extraction is still running. Come back in a moment.
                </div>
              )}
              {doc.status === "failed" && (
                <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded p-2 mb-3">
                  Extraction failed: {doc.processing?.error || "unknown error"}. Try re-running from the panel.
                </div>
              )}
              {blocks.length === 0 && doc.status !== "processing" && (
                <div className="text-sm text-zinc-500 py-6 text-center">
                  No blocks extracted from this document yet.
                </div>
              )}

              {grouped.map(([section, list]) => (
                <div key={section} className="mb-4">
                  <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-1">
                    {sectionLabel(section)}
                  </div>
                  <div className="space-y-2">
                    {list.map((b) => (
                      <BlockCard
                        key={b.block_id}
                        block={b}
                        disabled={isApproved || saving === b.block_id}
                        onMouseEnter={() => setHoverBlockId(b.block_id)}
                        onMouseLeave={() => setHoverBlockId(null)}
                        onPatch={(payload) => patchBlock(b.block_id, payload)}
                        saving={saving === b.block_id}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="border-t pt-3 flex-shrink-0 flex-wrap gap-2 sm:justify-between">
          <div className="text-xs text-zinc-500">
            {pendingSensitiveCount > 0
              ? <span className="text-red-700 font-medium flex items-center gap-1"><ShieldAlert className="h-3.5 w-3.5" />
                  {pendingSensitiveCount} sensitive field(s) still need review before approval
                </span>
              : blocks.length > 0
                ? <span>All sensitive fields reviewed. Ready to approve.</span>
                : null
            }
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={!doc || doc?.status === "processing" || blocks.length === 0}
              onClick={() => setCombinedOpen(true)}
              data-testid="hw-open-combined-btn"
            >
              <Merge className="h-3.5 w-3.5 mr-1" /> Combine with audio / intake
            </Button>
            <Button
              variant="outline"
              disabled={!doc || doc?.status === "processing" || blocks.length === 0}
              onClick={() => setMappingOpen(true)}
              data-testid="hw-open-mapping-btn"
            >
              <Users className="h-3.5 w-3.5 mr-1" /> Auto-populate record
            </Button>
            <Button
              variant="outline"
              disabled={!doc || doc?.status === "processing" || drafting || blocks.length === 0}
              onClick={draftSoap}
              data-testid="hw-generate-soap-btn"
            >
              {drafting
                ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Drafting…</>
                : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Generate draft SOAP</>
              }
            </Button>
            {!isApproved && (
              <Button
                onClick={approve}
                disabled={!canApprove || approving}
                data-testid="hw-approve-btn"
              >
                {approving
                  ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Approving…</>
                  : <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve document</>
                }
              </Button>
            )}
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </DialogFooter>

        <HandwritingMappingDialog
          uploadId={uploadId}
          open={mappingOpen}
          onOpenChange={setMappingOpen}
          onApplied={load}
        />
        <CombinedDraftDialog
          uploadId={uploadId}
          clientEmail={doc?.client_email}
          open={combinedOpen}
          onOpenChange={setCombinedOpen}
          onDrafted={load}
        />
      </DialogContent>
    </Dialog>
  );
}


// -----------------------------------------------------------------------
// One extracted block — inline editor + review action buttons
// -----------------------------------------------------------------------
function BlockCard({ block, onPatch, disabled, saving, onMouseEnter, onMouseLeave }) {
  const [value, setValue] = useState(
    block.confirmed_value || block.suggested_value || block.original_text || "",
  );
  useEffect(() => {
    setValue(block.confirmed_value || block.suggested_value || block.original_text || "");
  }, [block.confirmed_value, block.suggested_value, block.original_text]);

  const highlight = block.is_sensitive && block.review_status === "pending";

  return (
    <div
      className={cn(
        "border rounded p-2.5 bg-white",
        highlight ? "border-red-300 ring-1 ring-red-200 bg-red-50/30" : "border-zinc-200",
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid={`hw-block-${block.block_id}`}
    >
      <div className="flex items-start gap-2 mb-1.5">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-zinc-800 truncate">{block.label || "—"}</div>
          <div className="text-2xs text-zinc-500 truncate">
            <span className="font-mono">Original:</span> {block.original_text || "(none)"}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge className={cn("text-2xs uppercase border", CONFIDENCE_TONE[block.confidence] || CONFIDENCE_TONE.medium)}>
            {block.confidence}
          </Badge>
          <Badge className={cn("text-2xs uppercase border", REVIEW_TONE[block.review_status] || REVIEW_TONE.pending)}>
            {block.review_status}
          </Badge>
          {block.is_sensitive && (
            <Badge className="text-2xs uppercase border bg-red-50 text-red-700 border-red-200 flex items-center gap-1">
              <ShieldAlert className="h-3 w-3" /> Sensitive
            </Badge>
          )}
        </div>
      </div>

      <Textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={Math.min(4, Math.max(1, (value || "").length > 60 ? 3 : 1))}
        disabled={disabled}
        className="text-sm"
        data-testid={`hw-block-value-${block.block_id}`}
      />

      {!disabled && (
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          <Button
            size="sm" variant="outline"
            onClick={() => onPatch({
              review_status: value !== (block.suggested_value || block.original_text) ? "corrected" : "confirmed",
              confirmed_value: value,
            })}
            disabled={saving}
            data-testid={`hw-block-confirm-${block.block_id}`}
          >
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Confirm
          </Button>
          <Button
            size="sm" variant="ghost"
            onClick={() => onPatch({ review_status: "illegible", confidence: "illegible" })}
            disabled={saving}
            data-testid={`hw-block-illegible-${block.block_id}`}
          >
            <EyeOff className="h-3.5 w-3.5 mr-1" /> Illegible
          </Button>
          <Button
            size="sm" variant="ghost"
            onClick={() => onPatch({ review_status: "rejected" })}
            disabled={saving}
            data-testid={`hw-block-reject-${block.block_id}`}
          >
            <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
          </Button>
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400 ml-1" />}
        </div>
      )}
      {block.reviewer_name && (
        <div className="text-2xs text-zinc-400 mt-1">
          Reviewed by {block.reviewer_name} · {block.reviewed_at ? new Date(block.reviewed_at).toLocaleString() : ""}
        </div>
      )}
    </div>
  );
}
